import React, { FC, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  LottiePlayerECOMMBX,
  makeStyles,
  Theme,
  Typography,
} from '@dxb-web-ui-framework/design-system';
import { useTranslation } from 'react-i18next';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';

export type AppSuccessProps = {
  fullHeight?: boolean;
  mobileReversion?: boolean;
  message?: string;
  subtitle?: string;
  btnPrimaryLabel?: string;
  btnPrimaryOnClick?: () => void;
  btnSecondaryLabel?: string;
  btnSecondaryIcon?: any;
  btnSecondaryOnClick?: () => void;
  hasPrimaryButton?: boolean;
  className?: string;
};

const useStyles = makeStyles<Theme>(({ palette, spacing, breakpoints }) => ({
  header: {
    color: 'green',
  },
  section: ({ fullHeight, mobileReversion }: { fullHeight?: boolean; mobileReversion?: boolean }) => ({
    height: fullHeight ? '100vh' : 'auto',
    zIndex: 1,
    width: '100%',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    flex: 1,
    [breakpoints.down('sm')]: {
      flexDirection: mobileReversion ? 'column-reverse' : 'column',
    },
  }),
  lottiePlayerContainer: {
    overflow: 'hidden',
    maxHeight: '520px',
    width: '520px',
    [breakpoints.down('md')]: {
      marginBottom: spacing(2),
    },
    [breakpoints.down('sm')]: {
      maxHeight: '520px',
      width: '100%',
    },
  },
}));

const AppSuccess: FC<AppSuccessProps> = ({
  fullHeight,
  mobileReversion,
  message,
  subtitle,
  btnPrimaryLabel,
  btnPrimaryOnClick,
  btnSecondaryLabel,
  btnSecondaryIcon,
  btnSecondaryOnClick,
  hasPrimaryButton,
  className,
}) => {
  const classes = useStyles({ fullHeight, mobileReversion });
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const { t } = useTranslation();

  const onPrimaryButtonClick = useCallback(() => {
    if (btnPrimaryOnClick) {
      btnPrimaryOnClick();
    } else {
      // TODO add a fallback action
    }
  }, [btnPrimaryOnClick]);

  const onSecondaryButtonClick = useCallback(() => {
    if (btnSecondaryOnClick) {
      btnSecondaryOnClick();
    } else {
      // TODO add a fallback action
    }
  }, [btnSecondaryOnClick]);

  return (
    <Grid container className={`${classes.section} ${className}`}>
      <Box px={isMobile ? 4 : 0} width={isMobile ? '100%' : 'auto'}>
        <Typography component={'h1'} variant={isMobile ? 'h4' : 'h2'} className={classes.header}>
          {t('appSuccess.header')}
        </Typography>
        {message && (
          <Box pb={subtitle ? 3 : 6}>
            <Typography component={'h2'} variant={isMobile ? 'h6' : 'h5'} color={'secondary'}>
              {message}
            </Typography>
          </Box>
        )}
        {subtitle && (
          <Box pb={6}>
            <Typography component={'h3'} variant={'body2'} color={'textSecondary'}>
              {subtitle}
            </Typography>
          </Box>
        )}
        <Box display={'flex'} justifyContent={'flex-start'} alignItems={'center'}>
          {btnSecondaryLabel && (
            <Box mr={2} width={'100%'}>
              <Button
                color='primary'
                endIcon={btnSecondaryIcon}
                size='large'
                variant='outlined'
                fullWidth={isMobile}
                onClick={onSecondaryButtonClick}>
                {btnSecondaryLabel}
              </Button>
            </Box>
          )}
          {hasPrimaryButton && (
            <Box width={'100%'}>
              <Button
                color='primary'
                size='large'
                variant='contained'
                fullWidth={isMobile}
                onClick={onPrimaryButtonClick}>
                {btnPrimaryLabel}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <Box className={classes.lottiePlayerContainer}>
        <LottiePlayerECOMMBX icon='ecommbx_success' loop play />
      </Box>
    </Grid>
  );
};

export { AppSuccess };
